import clsx from 'clsx'
import React, {useState} from 'react'

import {Loader} from '../loader'
import {MapsContextProvider, useMapContextValue} from './context'
import {classes} from './Map.classes'
import {useCustomization} from './Map.hooks'
import {MapMargin} from './MapMargin'
import {MapProps as MapPropsBase, MapRef} from './types'

type MapProps = MapPropsBase &
  React.PropsWithChildren<{
    brand?: 'yandex' | 'yango'
    errorMessage?: string
    lang: string
    renderFallback?(): React.ReactElement
    theme?: 'light' | 'dark'
  }>

type MapProvidedProps = MapProps & {
  mapRef?: MapRef
  setMapRef: (ref: MapRef | null | undefined) => void
}

const defaultRenderFallback = () => <Loader />

const MapProvided: React.FC<MapProvidedProps> = ({
  children,
  renderFallback = defaultRenderFallback,
  theme = 'light',
  setMapRef,
  className,
  ...props
}) => {
  const mapContext = useMapContextValue()
  const customization = useCustomization(theme)

  if (!mapContext || !customization) {
    return renderFallback()
  }

  const {
    components: {YMap, YMapDefaultFeaturesLayer, YMapDefaultSchemeLayer},
  } = mapContext

  return (
    <YMap ref={setMapRef} className={clsx(classes.root, className)} copyrights={false} mode="vector" {...props}>
      <MapMargin margin={props.margin} />
      <YMapDefaultSchemeLayer customization={customization} />
      <YMapDefaultFeaturesLayer />
      {children}
    </YMap>
  )
}

export const Map: React.FC<MapProps> = ({brand = 'yandex', errorMessage, ...props}) => {
  const [mapRef, setMapRef] = useState<MapRef | null>()
  return (
    <MapsContextProvider
      brand={brand}
      errorMessage={errorMessage}
      lang={props.lang}
      mapRef={mapRef}
      setMapRef={setMapRef}
    >
      <MapProvided setMapRef={setMapRef} {...props} />
    </MapsContextProvider>
  )
}
